import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { EnumGlobalRoutes } from '@/app/router/routerConstants'
import { getUser } from '@/features/auth/authSlice'
import { useAppSelector } from '@/hooks/redux'
import { useLazyGetDispatcherUsersQuery } from '@/services/dispatcher/dispather'
import { useLazyGetExecutivesUsersQuery } from '@/services/executives/executives'
import type { ITenantUsersResponse } from '@/services/types'
import { UserRoleEnum } from '@/types'

import { useCreateChatByUserMutation } from '../../services/chats/chats'
import { useLazyGetAllUsersQuery } from '../../services/tenant/users'
import Button from '../common/Button'
import Modal from '../common/Modal'
import SearchInputWidthDrop from '../common/SearchInputWidthDrop'
import SelectedUsersList from '../common/SelectedUsersList'

interface INewChatModalProps {
    isOpen: boolean
    onClose: () => void
}

const NewChatModal = ({ isOpen, onClose }: INewChatModalProps) => {
    const me = useAppSelector(getUser)
    const navigate = useNavigate()
    const [getUsers] = useLazyGetAllUsersQuery()
    const [getDispatcherUsers] = useLazyGetDispatcherUsersQuery()
    const [getExecutivesUsers] = useLazyGetExecutivesUsersQuery()
    const [createChatByUser] = useCreateChatByUserMutation()
    const [selectedUser, setSelectedUser] =
        useState<ITenantUsersResponse | null>(null)

    const handelClose = () => {
        onClose()
    }

    const onSubmit = async () => {
        if (!selectedUser) {
            toast.error('At least one member must be added.')
            return
        }

        try {
            const formData = new FormData()
            formData.append('name', selectedUser.fullName)
            formData.append('content', `Hey ${selectedUser.fullName}`)

            const res = await createChatByUser({
                id: selectedUser.id,
                data: formData,
            }).unwrap()

            if (res?.success) {
                toast.success(res?.message || 'Chat created successfully!')
                navigate(`${EnumGlobalRoutes.CHAT}/${res?.data?.id}`)
                onClose()
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            toast.error('Something went wrong')
        }
    }

    const fetcherMap = {
        [UserRoleEnum.TENANT_ADMIN]: getUsers,
        [UserRoleEnum.DISPATCHER]: getDispatcherUsers,
        [UserRoleEnum.EXECUTIVES]: getExecutivesUsers,
    }

    const activeRole = window.localStorage.getItem('activeRole')
    const role =
        activeRole && Object.keys(fetcherMap).includes(activeRole)
            ? activeRole
            : undefined
    if (!role) return null

    const fetcher = fetcherMap[role as keyof typeof fetcherMap]

    return (
        <Modal isOpen={isOpen} onClose={handelClose} title={'New Chat'}>
            <div className="modal__body p-0">
                <div className="custom-form">
                    <div className="custom-form-body">
                        <div className="row gutters-16">
                            <div className="col-12">
                                <div className="search-widget overflow-visible">
                                    <div className="search-widget--head">
                                        <SearchInputWidthDrop
                                            placeholder="Add users by name or email..."
                                            label="Users"
                                            fetchFunk={fetcher}
                                            selectedItems={
                                                selectedUser
                                                    ? [selectedUser?.id]
                                                    : []
                                            }
                                            onSelected={(id) =>
                                                setSelectedUser(id)
                                            }
                                        />
                                    </div>
                                    <div className="search-widget--body">
                                        <SelectedUsersList
                                            list={
                                                selectedUser
                                                    ? [
                                                          {
                                                              id: selectedUser.id,
                                                              name: selectedUser.fullName,
                                                              email: selectedUser.email,
                                                              role: selectedUser
                                                                  .roles[0],
                                                              src:
                                                                  selectedUser.photo ||
                                                                  '',
                                                          },
                                                      ]
                                                    : []
                                            }
                                            removeAction={() =>
                                                setSelectedUser(null)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="custom-form-footer justify-end">
                        <div className="flex-group flex-group--xs">
                            <Button
                                type="button"
                                onClick={onSubmit}
                                size="lg"
                                variant="primary-gradient"
                            >
                                <span>Submit</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default NewChatModal
